<template>
  <main-drawer :title="isEdit ? $tc('tournament.edit_competitor') : $tc('tournament.register_competitor')" @close="$emit('close')">
    <main-select
      v-if="showClubSelect"
      v-model="form.clubId"
      :label="$tc('general.club')"
      :options="clubOptions"
      @input="onClubChange" />

    <main-select
      v-model="form.memberId"
      :label="$tc('tournament.competitors')"
      :disabled="isEdit"
      :options="memberOptions"
      :validator="$v.form.memberId" />

    <main-select
      v-if="form.memberId"
      v-model="form.categories"
      :label="$tc('settings.category', 2)"
      :options="categoryOptions"
      :validator="$v.form.categories"
      multiple />

    <template v-if="form.categories && !!form.categories.length">
      <main-select
        v-for="categoryId in form.categories"
        :key="categoryId"
        v-model="form.disciplines[categoryId]"
        :validator="$v.form.disciplines ? $v.form.disciplines[categoryId] : null"
        :label="`${$tc('settings.discipline', 2)} - ${getCategoryName(categoryId)}`"
        :options="getDisciplineOptions(categoryId)"
        multiple
        @input="$forceUpdate()" />
    </template>

    <div v-if="selectedMember" class="competitor-belt-info">
      <i class="el-icon-info" />
      <span>{{ $tc('form.belt') }}: {{ selectedMemberBeltDisplay }}</span>
    </div>

    <main-checkbox
      v-model="form.isSeeded"
      :label="$tc('tournament.seeded_competitor')" />
    <div class="helper-text">{{ $tc('tournament.seeded_competitor_description') }}</div>

    <template v-slot:footer>
      <div class="bottom-nav">
        <div class="flex" v-if="user.isAdmin">
          <span class="switch-label">{{ $tc('tournament.show_category_based') }}</span>
          <el-switch v-model="showCompetitorsBasedOnSelectedCategory" />
        </div>

        <div class="flex-buttons">
          <el-button @click="$emit('close')">{{ $tc('general.cancel') }}</el-button>
          <el-button type="primary" @click="save()">{{ isEdit ? $tc('general.edit') : $tc('general.save') }}</el-button>
        </div>
      </div>
    </template>
  </main-drawer>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'
import { required } from 'vuelidate/lib/validators'
import { belts } from '@/common/definitions'

export default {
  props: {
    tournament: {
      type: Object,
      required: true
    },

    club: {
      type: Object,
      default: null
    },

    competitor: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      form: {
        clubId: null,
        memberId: null,
        categories: null,
        disciplines: {},
        isSeeded: false
      },
      showCompetitorsBasedOnSelectedCategory: false,
      clubWithMembers: null
    }
  },

  created () {
    const storedValue = localStorage.getItem('showCompetitorsBasedOnSelectedCategory')
    this.showCompetitorsBasedOnSelectedCategory = storedValue === 'true'
    if (this.showClubSelect) {
      this.$store.dispatch('clubs/load')
    }
  },

  computed: {
    showClubSelect () {
      return !this.club && this.user?.isAdmin && !this.competitor
    },

    effectiveClub () {
      if (this.club) return this.club
      if (this.competitor?.club) return this.competitor.club
      if (this.showClubSelect && this.form.clubId) {
        return this.clubWithMembers || this.$store.getters['clubs/get'](this.form.clubId)
      }
      return null
    },

    clubOptions () {
      const list = this.$store.getters['clubs/list'] || []
      return list.filter(c => !c.isBlocked && !c.isDeleted).map(c => ({ id: c.id, text: c.name }))
    },

    isEdit () {
      return !!this.competitor
    },

    disciplineErrors () {
      const data = {}

      if (this.form.categories?.length) {
        this.form.categories.forEach(id => {
          data[id] = {
            required: !!this.form.disciplines[id]?.length,
            $dirty: this.$v.form.$dirty,
            $pending: this.$v.form.$pending,
            $invalid: !this.form.disciplines[id]?.length,
            $touch: () => {}
          }
        })
      }

      return data
    },

    selectedMember () {
      if (!this.form.memberId) return

      return this.members?.find(m => m.id === this.form.memberId)
    },

    selectedMemberBeltDisplay () {
      const raw = this.selectedMember?.belt
      if (raw == null || raw === '') return '—'
      const fromDefinitions = belts[raw]
      if (fromDefinitions) return fromDefinitions
      const key = Object.keys(belts).find(k => String(k).toUpperCase() === String(raw).toUpperCase())
      if (key) return belts[key]
      return String(raw).charAt(0).toUpperCase() + String(raw).slice(1).toLowerCase()
    },

    tournamentDisciplines () {
      return this.tournament.disciplines.map(d => d.discipline)
    },

    categoryOptions () {
      const categoryIdsObject = {}
      this.tournamentDisciplines.forEach(d => {
        d.disciplineCategories.forEach(cat => {
          categoryIdsObject[cat.categoryId] = true
        })
      })

      let tournamentCategories = this.allCategories.filter(category => Object.keys(categoryIdsObject).includes(category.id + '')) || []
      if (this.effectiveFilterByCategory && this.selectedMember?.born) {
        const age = this.getAgeFromBirthYear(this.selectedMember.born)
        if (age != null) {
          tournamentCategories = tournamentCategories.filter(category => {
            return age >= category.ageFrom && (category.ageTo ? age <= category.ageTo : true)
          })
        }
      }

      return tournamentCategories.map(category => ({
        id: category.id,
        text: category.name
      }))
    },

    allCategories () {
      return this.$store.state.categories.data
    },

    memberOptions () {
      let filtered = this.members?.filter(member => member.isActive) || []
      if (this.effectiveFilterByCategory && this.form.categories?.length) {
        filtered = filtered.filter(member => this.isMemberEligibleForSelectedCategories(member))
      }
      return filtered.map(member => ({
        id: member.id,
        text: member.firstName + ' ' + member.lastName
      }))
    },

    members () {
      return this.effectiveClub?.members || []
    },

    user () {
      return this.$store.state.auth.user
    },

    effectiveFilterByCategory () {
      return !this.user.isAdmin || this.showCompetitorsBasedOnSelectedCategory
    }
  },

  watch: {
    competitor: {
      handler (competitor) {
        if (!competitor) return

        const normalizedCategories = Array.isArray(competitor.categories) ? competitor.categories : []
        const categories = normalizedCategories
          .map(i => i.categoryId ?? i.category?.id)
          .filter(id => id != null)

        const disciplines = normalizedCategories.reduce((acc, i) => {
          const categoryId = i.categoryId ?? i.category?.id
          if (categoryId == null) return acc
          const disciplineIds = (i.disciplines || [])
            .map(d => d.disciplineId ?? d.discipline?.id)
            .filter(id => id != null)
          acc[categoryId] = disciplineIds
          return acc
        }, {})

        this.form = {
          memberId: competitor.competitorId,
          categories,
          disciplines,
          isSeeded: competitor.is_seeded || false
        }
      },
      immediate: true
    },

    'form.memberId': {
      handler (memberId, previousMemberId) {
        if (previousMemberId !== undefined && memberId !== previousMemberId) {
          this.form.categories = null
          this.form.disciplines = {}
        }
      }
    },

    'form.categories': {
      handler (categories) {
        if (categories?.length) {
          categories.forEach(categoryId => {
            if (!this.form.disciplines[categoryId]) {
              this.form.disciplines[categoryId] = []
              this.$forceUpdate()
            }
          })
        }
      },
      deep: true
    },
    showCompetitorsBasedOnSelectedCategory (value) {
      localStorage.setItem('showCompetitorsBasedOnSelectedCategory', value.toString())
    },

    categoryOptions (options) {
      if (!this.form.categories?.length || !options.length) return
      const validIds = new Set(options.map(o => Number(o.id)))
      const kept = this.form.categories.filter(id => validIds.has(Number(id)))
      if (kept.length !== this.form.categories.length) {
        this.form.categories = kept.length ? kept : null
        const nextDisciplines = {}
        Object.keys(this.form.disciplines).forEach(id => {
          if (validIds.has(Number(id))) nextDisciplines[id] = this.form.disciplines[id]
        })
        this.form.disciplines = nextDisciplines
      }
    }
  },

  methods: {
    onClubChange () {
      this.form.memberId = null
      this.form.categories = null
      this.form.disciplines = {}
      this.loadClubMembers(this.form.clubId)
    },

    async loadClubMembers (clubId) {
      if (!clubId) {
        this.clubWithMembers = null
        return
      }
      try {
        await this.$store.dispatch('clubs/get', clubId)
        this.clubWithMembers = this.$store.getters['clubs/get'](clubId)
      } catch (e) {
        this.clubWithMembers = null
        console.error(e)
      }
    },

    getAgeFromBirthYear (birthDate) {
      if (!birthDate) return null
      const birthYear = new Date(birthDate).getFullYear()
      return new Date().getFullYear() - birthYear
    },

    calculateAge (birthDate) {
      if (!birthDate) return null

      const birth = new Date(birthDate)
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const monthDiff = today.getMonth() - birth.getMonth()

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
      }

      return age
    },

    getDisciplineOptions (categoryId) {
      const tournamentDisciplines = this.tournamentDisciplines.map(tournamentDiscipline => ({
        ...tournamentDiscipline,
        categoryIds: tournamentDiscipline.disciplineCategories.map(category => category.categoryId)
      }))

      const disciplines = tournamentDisciplines.filter(d => {
        if (!d.categoryIds.includes(categoryId)) return false
        if (this.effectiveFilterByCategory) {
          const sexMatch = !d?.forSex || d.forSex === (this.selectedMember?.isMale ? '1' : '2')
          return sexMatch
        }
        return true
      })

      return disciplines.map(discipline => ({
        id: discipline.id,
        text: discipline.name
      }))
    },

    isMemberEligibleForSelectedCategories (member) {
      const memberAge = member?.born != null ? this.getAgeFromBirthYear(member.born) : null
      const memberIsMale = member?.isMale

      for (const categoryId of this.form.categories || []) {
        const category = this.allCategories.find(c => (c.id + '') === (categoryId + ''))
        if (category && (category.ageFrom != null || category.ageTo != null)) {
          if (memberAge == null) return false
          if (memberAge < category.ageFrom) return false
          if (category.ageTo != null && memberAge > category.ageTo) return false
        }

        const disciplineIds = this.form.disciplines?.[categoryId] || []
        const tournamentDisciplinesWithCategory = this.tournamentDisciplines.map(d => ({
          ...d,
          categoryIds: d.disciplineCategories?.map(c => c.categoryId) || []
        })).filter(d => d.categoryIds.includes(Number(categoryId)) || d.categoryIds.includes(categoryId))

        for (const disciplineId of disciplineIds) {
          const discipline = tournamentDisciplinesWithCategory.find(d => (d.id + '') === (disciplineId + ''))
          if (discipline?.forSex && discipline.forSex !== (memberIsMale ? '1' : '2')) return false
        }
      }
      return true
    },

    getCategoryName (categoryId) {
      return this.categoryOptions.find(category => category.id === categoryId)?.text
    },

    checkDisciplines () {
      return this.form.categories.every(id => !!this.form.disciplines?.[id]?.length)
    },

    async save () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      if (!this.checkDisciplines()) return this.$message.error(this.$tc('messages.error_enter_at_least_one_discipline'))

      this.loading = true

      const payload = {
        memberId: this.form.memberId,
        categories: this.form.categories.map(categoryId => ({
          id: categoryId,
          disciplines: this.form.disciplines[categoryId] || []
        })),
        is_seeded: this.form.isSeeded || false
      }

      try {
        let tournament
        if (this.isEdit) {
          tournament = await this.$store.dispatch('tournaments/updateCompetitor', {
            id: this.competitor.id,
            payload
          })
        } else {
          tournament = await this.$store.dispatch('tournaments/registerCompetitor', {
            id: this.tournament?.id,
            payload
          })
        }
        this.$message.success(this.isEdit ? this.$tc('messages.success_updated_competitor') : this.$tc('messages.success_registered_competitor'))
        this.$emit(this.isEdit ? 'edit' : 'add', tournament)
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    }
  },

  validations () {
    const rules = {
      form: {
        memberId: { required },
        categories: { required },
        disciplines: {}
      }
    }

    if (this.form.categories) {
      this.form.categories.forEach(id => {
        rules.form.disciplines[id] = { required }
      })
    }

    return rules
  }
}
</script>

<style lang="scss" scoped>
.bottom-nav {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
}

.flex {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.switch-label {
  font-size: 14px;
  color: #606266;
}

.helper-text {
  font-size: 12px;
  color: #909399;
  margin-top: -10px;
  margin-bottom: 10px;
  padding-left: 22px;
}

.competitor-belt-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #909399;
  margin-bottom: 14px;
  padding-left: 22px;
  text-transform: none;

  .el-icon-info {
    color: #909399;
    font-size: 16px;
  }
}
</style>
